import * as z from 'zod';

export const CreateBadgeRequest = z.object({
  badge: z
    .object({
      _id: z
        .string()
        .describe('Badge ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
        )
        .optional()
        .nullable(),
      title: z
        .string()
        .describe("Badge title displayed on the member's profile.")
        .min(1)
        .max(50),
      description: z
        .string()
        .describe(
          'Badge description explaining its purpose or criteria for earning it.'
        )
        .max(1500)
        .optional()
        .nullable(),
      backgroundColor: z
        .string()
        .describe('Badge background color in hexadecimal RGB format.'),
      textColor: z
        .string()
        .describe('Badge text color in hexadecimal RGB format.'),
      icon: z
        .string()
        .describe(
          'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
        )
        .optional(),
      permissionsEnabled: z
        .boolean()
        .describe(
          'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
        )
        .min(1)
        .max(255)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the badge was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the badge was last updated.')
        .optional()
        .nullable(),
    })
    .describe('Badge to create.'),
});
export const CreateBadgeResponse = z.object({
  _id: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe("Badge title displayed on the member's profile.")
    .min(1)
    .max(50)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Badge description explaining its purpose or criteria for earning it.'
    )
    .max(1500)
    .optional()
    .nullable(),
  backgroundColor: z
    .string()
    .describe('Badge background color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  textColor: z
    .string()
    .describe('Badge text color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  icon: z
    .string()
    .describe(
      'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
    )
    .optional(),
  permissionsEnabled: z
    .boolean()
    .describe(
      'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
    )
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
    )
    .min(1)
    .max(255)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the badge was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the badge was last updated.')
    .optional()
    .nullable(),
});
export const GetBadgeRequest = z.object({
  badgeId: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetBadgeResponse = z.object({
  _id: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe("Badge title displayed on the member's profile.")
    .min(1)
    .max(50)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Badge description explaining its purpose or criteria for earning it.'
    )
    .max(1500)
    .optional()
    .nullable(),
  backgroundColor: z
    .string()
    .describe('Badge background color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  textColor: z
    .string()
    .describe('Badge text color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  icon: z
    .string()
    .describe(
      'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
    )
    .optional(),
  permissionsEnabled: z
    .boolean()
    .describe(
      'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
    )
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
    )
    .min(1)
    .max(255)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the badge was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the badge was last updated.')
    .optional()
    .nullable(),
});
export const QueryBadgesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          title: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          permissionsEnabled: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'title',
                'description',
                'permissionsEnabled',
                'slug',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options including filters, sorting, and paging.'),
});
export const QueryBadgesResponse = z.object({
  badges: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Badge ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
          )
          .optional()
          .nullable(),
        title: z
          .string()
          .describe("Badge title displayed on the member's profile.")
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Badge description explaining its purpose or criteria for earning it.'
          )
          .max(1500)
          .optional()
          .nullable(),
        backgroundColor: z
          .string()
          .describe('Badge background color in hexadecimal RGB format.')
          .optional()
          .nullable(),
        textColor: z
          .string()
          .describe('Badge text color in hexadecimal RGB format.')
          .optional()
          .nullable(),
        icon: z
          .string()
          .describe(
            'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
          )
          .optional(),
        permissionsEnabled: z
          .boolean()
          .describe(
            'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
          )
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
          )
          .min(1)
          .max(255)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the badge was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the badge was last updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata for retrieving additional results.')
    .optional(),
});
export const UpdateBadgeRequest = z.object({
  _id: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  badge: z
    .object({
      _id: z
        .string()
        .describe('Badge ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
        )
        .optional()
        .nullable(),
      title: z
        .string()
        .describe("Badge title displayed on the member's profile.")
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Badge description explaining its purpose or criteria for earning it.'
        )
        .max(1500)
        .optional()
        .nullable(),
      backgroundColor: z
        .string()
        .describe('Badge background color in hexadecimal RGB format.')
        .optional()
        .nullable(),
      textColor: z
        .string()
        .describe('Badge text color in hexadecimal RGB format.')
        .optional()
        .nullable(),
      icon: z
        .string()
        .describe(
          'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
        )
        .optional(),
      permissionsEnabled: z
        .boolean()
        .describe(
          'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
        )
        .min(1)
        .max(255)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the badge was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the badge was last updated.')
        .optional()
        .nullable(),
    })
    .describe(
      'Badge to update. Must include ID and revision to prevent conflicts.'
    ),
});
export const UpdateBadgeResponse = z.object({
  _id: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe("Badge title displayed on the member's profile.")
    .min(1)
    .max(50)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Badge description explaining its purpose or criteria for earning it.'
    )
    .max(1500)
    .optional()
    .nullable(),
  backgroundColor: z
    .string()
    .describe('Badge background color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  textColor: z
    .string()
    .describe('Badge text color in hexadecimal RGB format.')
    .optional()
    .nullable(),
  icon: z
    .string()
    .describe(
      'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
    )
    .optional(),
  permissionsEnabled: z
    .boolean()
    .describe(
      'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
    )
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
    )
    .min(1)
    .max(255)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the badge was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the badge was last updated.')
    .optional()
    .nullable(),
});
export const DeleteBadgeRequest = z.object({
  badgeId: z
    .string()
    .describe('Badge ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteBadgeResponse = z.object({});
export const UpdateBadgesDisplayOrderRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
});
export const UpdateBadgesDisplayOrderResponse = z.object({
  badges: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Badge ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.'
          )
          .optional()
          .nullable(),
        title: z
          .string()
          .describe("Badge title displayed on the member's profile.")
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Badge description explaining its purpose or criteria for earning it.'
          )
          .max(1500)
          .optional()
          .nullable(),
        backgroundColor: z
          .string()
          .describe('Badge background color in hexadecimal RGB format.')
          .optional()
          .nullable(),
        textColor: z
          .string()
          .describe('Badge text color in hexadecimal RGB format.')
          .optional()
          .nullable(),
        icon: z
          .string()
          .describe(
            'Badge icon image.\n\nAny image format is accepted with automatic conversion and resizing handled by the system.\nSVG format is recommended as it is resolution independent and looks great at any scale.'
          )
          .optional(),
        permissionsEnabled: z
          .boolean()
          .describe(
            'Whether the badge grants special permissions to access specific members-only pages.\n\nWhen `true`, members with this badge receive special permissions.\nSite owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).'
          )
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'URL-friendly slug for the badge, automatically generated from the title.\n\nGenerated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.'
          )
          .min(1)
          .max(255)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the badge was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the badge was last updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const MoveBadgeRequest = z.object({
  badgeId: z
    .string()
    .describe('ID of the badge to move.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      position: z.enum(['LAST', 'FIRST', 'AFTER']).optional(),
      moveAfterBadgeId: z
        .string()
        .describe('ID of the badge after which to position your badge.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const MoveBadgeResponse = z.object({});
